import React, { useState, useEffect, useMemo } from 'react';
import { Avatar, Box, Stack } from '@mui/material';
import Topbar from "../../../../../../../component/TopBar";
import BtnSecondary from "../../../../../../../component/SecondaryButton";
import BtnPrimary from "../../../../../../../component/PrimaryButton";
import SectionLoader from "../../../../../../../component/SectionLoader";
import EmptyState from "../../../../../../../component/EmptyState";
import ActionDialog from '../../../../../../../component/ActionDialog';
import { StatusChip } from '../../../../../../../component/listing';
import { getLeaveRequestById, reviewLeaveRequest, getAllLeaveType } from '../../../../../../../services/leave';
import { fetchEmployeesByIdArray } from '../../../../../../../services/employee';
import { useParams } from 'react-router';
import { findDepartment } from '../../../../../../../utils/findDepartment';
import { useNotify } from '../../../../../../../component/NotificationProvider';
import { tokens, radius } from '../../../../../../../theme';

const STATUS_TONE = { Pending: "warning", Approved: "success", Rejected: "danger" };
const initialsOf = (n) => (n || "?").split(" ").filter(Boolean).map((p) => p[0]).join("").slice(0, 2).toUpperCase();
const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : "—");

const Field = ({ label, children }) => (
  <Box>
    <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "0.5rem", textTransform: "uppercase", letterSpacing: "0.03em" }}>{label}</Box>
    <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text }}>{children ?? "—"}</Box>
  </Box>
);

const RequestDetails = () => {
  const notify = useNotify();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  const [leaveData, setLeaveData] = useState(null);
  const [typeById, setTypeById] = useState({});
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await getLeaveRequestById(id);
        const req = res?.data || null;
        if (!active) return;
        setLeaveData(req);
        const [empRes, typeRes] = await Promise.all([
          req?.employeeId ? fetchEmployeesByIdArray([req.employeeId]) : Promise.resolve(null),
          getAllLeaveType(),
        ]);
        if (!active) return;
        setEmployee(empRes?.data?.[0] || null);
        const tm = {}; (typeRes?.data || []).forEach((t) => { tm[t._id] = t.name; });
        setTypeById(tm);
      } catch (error) {
        if (active) notify.error(error?.message || "Failed to load leave request");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const name = useMemo(() => employee ? `${employee.fname || ""} ${employee.lname || ""}`.trim() : "Unknown", [employee]);
  const dept = employee ? (findDepartment(employee.department)?.name || employee.department || "—") : "—";

  if (loading) return <Box sx={{ p: "1rem" }}><SectionLoader minHeight="50vh" /></Box>;
  if (!leaveData) return <Box sx={{ p: "1rem" }}><EmptyState title="Leave request not found" /></Box>;

  const isPending = leaveData.status === "Pending";
  const cardSx = { background: tokens.card, border: `1px solid ${tokens.border}`, borderRadius: `${radius.card}px`, p: "1.5rem", mb: "1.25rem" };

  return (
    <Box sx={{ width: "100%" }}>
      <Topbar MainHeading={"Leave"} breadcrumbs={[{ label: "Leave", href: "" }, { label: "Dashboard", href: "/app/leave-dashboard" }, { label: "Request" }]} />

      {/* employee header */}
      <Box sx={cardSx}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar src={employee?.profileImage || undefined} sx={{ width: "3rem", height: "3rem", bgcolor: tokens.accent, fontWeight: 600 }}>{initialsOf(name)}</Avatar>
            <Box>
              <Box sx={{ fontSize: "1rem", fontWeight: 600, color: tokens.text }}>{name}</Box>
              <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{[employee?.aliasId, dept].filter(Boolean).join(" · ") || "—"}</Box>
            </Box>
          </Stack>
          <StatusChip label={leaveData.status} tone={STATUS_TONE[leaveData.status]} />
        </Stack>
      </Box>

      {/* request specifics */}
      <Box sx={cardSx}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, gap: "1.5rem" }}>
          <Field label="Leave Type">{typeById[leaveData.leaveType] || "—"}</Field>
          <Field label="Start Date">{fmt(leaveData.firstDay)}</Field>
          <Field label="End Date">{fmt(leaveData.lastDay)}</Field>
          <Field label="Total Days">{leaveData?.days?.length || 0}</Field>
        </Box>
        <Box sx={{ mt: "1.5rem", pt: "1.25rem", borderTop: `1px solid ${tokens.border}` }}>
          <Field label="Reason"><Box sx={{ fontWeight: 400 }}>{leaveData.reason || "—"}</Box></Field>
        </Box>
        {leaveData.reviewerComment && (
          <Box sx={{ mt: "1.25rem" }}>
            <Field label="Reviewer comment"><Box sx={{ fontWeight: 400 }}>{leaveData.reviewerComment}</Box></Field>
          </Box>
        )}
      </Box>

      {isPending && (
        <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
          <BtnSecondary onClick={() => setRejectDialogOpen(true)} sx={{ borderRadius: "8px", color: tokens.danger, borderColor: tokens.dangerBorder }}>
            Reject
          </BtnSecondary>
          <BtnPrimary onClick={() => setApproveDialogOpen(true)} sx={{ borderRadius: "8px" }}>
            Approve
          </BtnPrimary>
        </Stack>
      )}

      <ActionDialog type="approve" open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} reviewApi={reviewLeaveRequest} id={leaveData?._id} text="Approve Leave?" />
      <ActionDialog type="reject" open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} reviewApi={reviewLeaveRequest} id={leaveData?._id} text="Reject Leave?" />
    </Box>
  );
};

export default RequestDetails;
